import { Component } from '@theme/component';

/**
 * A lightweight image viewer for the product card "preview" dialog.
 * Stacks all images absolutely and crossfades between them — avoids reusing
 * the full slideshow component (built for full-viewport carousels, whose
 * dvh-based sizing doesn't behave inside a small modal).
 *
 * On color-split catalog cards, keeps only photos for that color.
 *
 * @extends Component<{ slides: HTMLElement[] }>
 */
export class ProductPreviewViewer extends Component {
  requiredRefs = ['slides'];

  /** @type {number} */
  #index = 0;

  /** @type {boolean} */
  #filtered = false;

  connectedCallback() {
    super.connectedCallback();
    const host = this.closest('dialog-component');
    host?.addEventListener('dialog:open', this.#onDialogOpen);
    this.#filterByCardColor();
  }

  disconnectedCallback() {
    const host = this.closest('dialog-component');
    host?.removeEventListener('dialog:open', this.#onDialogOpen);
    super.disconnectedCallback?.();
  }

  #onDialogOpen = () => {
    this.#filterByCardColor();
  };

  /**
   * Shows the next image.
   */
  next() {
    this.#filterByCardColor();
    this.#show(this.#index + 1);
  }

  /**
   * Shows the previous image.
   */
  previous() {
    this.#filterByCardColor();
    this.#show(this.#index - 1);
  }

  /**
   * Keep only slides that belong to this catalog card's color.
   */
  #filterByCardColor() {
    if (this.#filtered) return;

    const card = this.closest('product-card');
    const colorValue = card?.dataset?.initialColorValue;
    if (!colorValue || !card) {
      this.#filtered = true;
      return;
    }

    const colorToken = `|${colorValue}|`;
    const allowedFiles = new Set();
    const variantsScript = card.querySelector('script[data-card-variants]');
    const colorPosition = Number(card.dataset.initialColorPosition) - 1;

    const fileFromUrl = (url) => {
      if (!url) return '';
      try {
        return String(url).split('/').pop().split('?')[0].toLowerCase();
      } catch {
        return '';
      }
    };

    if (variantsScript instanceof HTMLScriptElement) {
      try {
        const variants = JSON.parse(variantsScript.textContent || '[]');
        for (const variant of variants) {
          // script[data-card-variants] is already scoped to this card's color
          const optionKey = `option${colorPosition + 1}`;
          const matchesColor =
            colorPosition < 0 ||
            variant.options?.[colorPosition] === colorValue ||
            variant[optionKey] === colorValue ||
            !variant.options;
          if (!matchesColor) continue;

          const src =
            variant?.featured_image?.src ||
            variant?.featured_media?.preview_image?.src ||
            variant?.featured_media?.src;
          const file = fileFromUrl(src);
          if (file) allowedFiles.add(file);
        }
      } catch {
        // fall back to data-media-colors only
      }
    }

    const slides = Array.from(this.querySelectorAll('.product-preview__image'));
    let kept = 0;
    /** @type {HTMLElement[]} */
    const removed = [];

    for (const slide of slides) {
      if (!(slide instanceof HTMLElement)) continue;

      const colors = slide.getAttribute('data-media-colors') || '';
      const hasColorTags = colors.replace(/\|/g, '').trim() !== '';
      const file = fileFromUrl(slide.getAttribute('data-media-file'));
      const taggedForThisColor = hasColorTags && colors.includes(colorToken);
      const taggedForOtherColor = hasColorTags && !colors.includes(colorToken);
      const matchesFile = file !== '' && allowedFiles.has(file);

      if ((taggedForOtherColor && !matchesFile) || (!hasColorTags && !matchesFile)) {
        removed.push(slide);
        continue;
      }

      if (!(taggedForThisColor || matchesFile)) {
        removed.push(slide);
        continue;
      }

      slide.hidden = false;
      slide.classList.toggle('product-preview__image--active', kept === 0);
      kept += 1;
    }

    // Never leave the preview empty — fall back to full set if matching failed.
    if (kept === 0) {
      this.#filtered = true;
      return;
    }

    for (const slide of removed) {
      slide.remove();
    }

    this.#index = 0;
    this.#filtered = true;
    this.#syncNav(kept);

    const title = this.closest('dialog')?.querySelector('.product-preview__title');
    if (title && colorValue && !title.textContent?.includes(colorValue)) {
      title.textContent = `${title.textContent?.trim() || ''} — ${colorValue}`;
    }
  }

  /**
   * @param {number} count
   */
  #syncNav(count) {
    const dialog = this.closest('dialog');
    if (!dialog) return;
    dialog.querySelectorAll('.product-preview__nav').forEach((nav) => {
      if (nav instanceof HTMLElement) {
        nav.hidden = count <= 1;
      }
    });
  }

  /**
   * @param {number} index
   */
  #show(index) {
    const slides = Array.from(this.querySelectorAll('.product-preview__image'));
    const count = slides.length;
    if (count === 0) return;

    this.#index = (index + count) % count;

    slides.forEach((slide, i) => {
      slide.classList.toggle('product-preview__image--active', i === this.#index);
    });
  }
}

if (!customElements.get('product-preview-viewer')) {
  customElements.define('product-preview-viewer', ProductPreviewViewer);
}
